/* eslint-disable */
import { useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import saveAs from "file-saver";

import {
  ChevronRight,
  DocumentText,
  Insurance,
  ShieldCheck,
  Stepper25perc,
  Stepper50perc,
  Stepper75perc,
} from "../../common/icons";
import { LocalizationContext } from "../../App";
import { DeclarationType, ProductType } from "../../language/localization";
import {
  AmendmentSubmissionDto,
  CustomerInputDto,
  SharedPolicyAmendmentDto,
} from "../../models/amendmentSubmissionDto";
import { SubmitAmendmentRequest } from "../../models/request/submitAmendmentRequest";
import { useCanAmendPolicyQuery, useDownloadPolicyDocumentQuery, useGetPoliciesDocumentsMutation, useSubmitAmendmentMutation } from "../../store/policiesSlice";
import { SplashMessage } from "../../common/SplashMessage";
import { PolicyAmendment } from "../../components/policy/amendments/PolicyAmendment";
import { Formatters } from "../../common/helpers/formatters";
import { useGetCustomerDetailsQuery } from "../../store/userSlice";
import { RootState, useAppSelector } from "../../store/store";
import { ProductContext } from "../../context/ProductContext";
import { AmendPolicy } from "../../models/amendPolicy";
import { DocumentEntityRequest } from "../../models/request/documentEntityRequest";
import { DocumentRequest, DownloadDocumentRequest } from "../../models/request/documentRequest";
import { fileExtensionRegex } from "../../common/constants";
import { environment } from "../../environments/environment";

interface StepDefinition {
  stepNumber: number;
  icon: JSX.Element;
  title: string;
  subtitle?: string;
}

export const ProductList = () => {
  const navigate = useNavigate();
  const { ppi, productIdToTypeKeyDict } = useContext(ProductContext);
  const l10n = useContext(LocalizationContext);
  const { data: profile, isLoading: isProfileLoading } = useGetCustomerDetailsQuery();
  const [finalDeclarations] = useState<DeclarationType[]>(["gtc", "vag45"]);
  const [finalApproval, setFinalApproval] = useState(false);
  const [selectedPolicies, setSelectedPolicies] = useState<string[]>([]);
  const [isValid, setAsValid] = useState(false);
  const [stepsDefinition, setSteps] = useState<StepDefinition[]>([]);
  const [step, setStep] = useState(1);
  const isSummaryStep = !!stepsDefinition.length && step === stepsDefinition.length;
  const selectedLanguage = useAppSelector((state) => state.app.appLanguage);
  const {
    data: policiesToAmend,
    isLoading: arePoliciesLoading,
    isSuccess: arePoliciesFetched,
  } = useCanAmendPolicyQuery(undefined, { refetchOnMountOrArgChange: true });
  const isDataLoading = isProfileLoading || arePoliciesLoading;
  const [
    submitAmendment,
    { isError: isAmendmentFailed, isLoading: isAmendmentLoading, isSuccess: isAmendmentSuccess },
  ] = useSubmitAmendmentMutation();

  const { monthlyPriceDi, monthlyGrossPriceDi, monthlyGrossInstallmentPriceDi,
    monthlyPricePpi, monthlyGrossPricePpi, monthlyGrossInstallmentPricePpi } = useAppSelector((state: RootState) => state.app);
  const totalMonthlyPrice =
    (selectedPolicies.includes("DI") ? monthlyGrossPriceDi : 0) +
    (selectedPolicies.includes("PPI") ? monthlyGrossPricePpi : 0);

  const [getMultipleDocuments, { data: documents, isLoading: isLoadingDocuments }] =
    useGetPoliciesDocumentsMutation();
  const [downloadDocumentRequest, setDownloadDocumentRequest] = useState<
    DownloadDocumentRequest | undefined
  >(undefined);
  const { data: downloadedDocument, originalArgs: downloadDocumentArgs } = useDownloadPolicyDocumentQuery(
    downloadDocumentRequest ?? skipToken,
    { refetchOnMountOrArgChange: true }
  );

  useEffect(() => {
    if (arePoliciesFetched && policiesToAmend?.length === 0) navigate("/");

    const newSteps: StepDefinition[] = [];
    const docEntityRequestArray: DocumentEntityRequest[] = [];

    const processingPolicies = localStorage["policiesAmended"] ? JSON.parse(localStorage["policiesAmended"]) as {time: Date, policies: number[]} : {time: new Date(), policies: []};
    policiesToAmend?.filter(x => !processingPolicies.policies.includes(x.id)).forEach((p, index, arr) => {
      let productType: ProductType = productIdToTypeKeyDict[p.productTypeId];
      let nextStep: string =
        index + 1 === arr.length ? l10n.title.summary : l10n.products[productIdToTypeKeyDict[arr[index + 1].productTypeId]].name;

      newSteps.push({
        stepNumber: newSteps.length + 1,
        icon:
          newSteps.length === 0 ? (
            <Stepper25perc className={"scale-50"} />
          ) : (
            <Stepper50perc className={"scale-50"} />
          ),
        title: `${l10n.common.your} ${l10n.products[productType].customName}`,
        subtitle: `${l10n.common.next}: ${nextStep}`,
      });

      docEntityRequestArray.push({
        entityType: "1",
        entityIdentifier: p.uniqueIdentifier
      });
    });

    newSteps.push({
      stepNumber: newSteps.length + 1,
      icon:
        newSteps.length === 1 ? (
          <Stepper50perc className={"scale-50"} />
        ) : (
          <Stepper75perc className={"scale-50"} />
        ),
      title: l10n.title.summary,
    });

    setSteps(newSteps);

    if (docEntityRequestArray && docEntityRequestArray.length > 0) {
      const documentRequest: DocumentRequest = {
        documents: docEntityRequestArray,
      };
      getMultipleDocuments(documentRequest);
    }
  }, [arePoliciesFetched, policiesToAmend, l10n]);

  const changeStep = (action: "forward" | "cancel") => {
    if (isValid && action === "forward") {
      if (policiesToAmend?.at(step - 1))
        setSelectedPolicies((v) => {
          const ns = [...v];
          ns.push(
            policiesToAmend?.at(step - 1)?.productTypeId === ppi ? "PPI" : "DI"
          );
          return ns;
        });

      if (!isSummaryStep) {
        // Navigate to next step
        setStep((v) => v + 1);
        setAsValid(false);
      } else {
        // Submit amendment
        const arrayAmendData: Array<AmendmentSubmissionDto> = new Array<AmendmentSubmissionDto>();
        let brokerCode:string= "";
        policiesToAmend?.forEach((p) => {
          if (!selectedPolicies.includes(p.productTypeId === ppi ? "PPI" : "DI"))
            return;

          brokerCode = p.brokerCodeId;
          const amendData: AmendmentSubmissionDto = {
            isInFuture: false,
            isRenewal: false,
            scheduledAt: null,
            priceRecalculation: false,
            policyUniqueIdentifier: p.uniqueIdentifier,
            productVersionCode: p.productVersionCode,
            amendmentVersionCode: p.amendmentVersionCode,
            policyAmendmentData: p.policyDetails,
          };

          arrayAmendData.push(amendData);
        });

        const customerData: CustomerInputDto =
          (profile && {
            title: profile.title,
            birthDate: profile.birthDate,
            branch: profile.branch,
            sensitiveStatus: profile.sensitiveStatus,
            protectedStatus: profile.protectedStatus,
            gender: profile.gender,
            group: profile.group,
            customerStatus: profile.customerStatus,
            maritalStatus: profile.maritalStatus,
            language: profile.language,
            emailType: profile.emailType,
            phoneNumber: profile.phoneNumber,
            phoneType: profile.phoneType,
            street: profile.street,
            streetNumber: profile.streetNumber,
            zipCode: profile.zipCode,
            region: profile.region,
            city: profile.city,
            state: profile.state,
            country: profile.country,
            addressType: profile.addressType,
            salutation: profile.salutation,
            acquisitionStatus: profile.acquisitionStatus,
            acquisitionSubStatus: profile.acquisitionSubStatus,
            leadSource: profile.leadSource,
            companySize: profile.companySize,
            companyIndustryType: profile.companyIndustryType,
            employmentRole: profile.employmentRole,
            jobTitle: profile.jobTitle,
            decisionMakerStatus: profile.decisionMakerStatus,
            externalReference1: profile.externalReference1,
            externalReference2: profile.externalReference2,
            customerCode: profile.customerCode,
            customerType: profile.customerType,
            email: profile.email,
            firstName: profile.firstName,
            lastName: profile.lastName,
          }) ||
          ({} as CustomerInputDto);

        const customerSharedData: SharedPolicyAmendmentDto = {
          brokerCode: brokerCode,
          customerData: customerData,
          language: selectedLanguage ?? "de",
        };

        const amendmentRequest: SubmitAmendmentRequest = {
          sharedData: customerSharedData,
          amendmentData: arrayAmendData,
        };

        submitAmendment(amendmentRequest);
      }
    } else if (action === "cancel" && step <= (policiesToAmend?.length || 0)) {
      setStep((v) => v + 1);
      setAsValid(false);

      if (step == policiesToAmend?.length && selectedPolicies.length === 0) navigate("/");
    } else if (action === "cancel") {
      navigate("/");
    }
  };

  const [summaryExpandedProducts, setExpandSummaryProducts] = useState<string[]>([]);

  const expandProductSummary = (product: string) => {
    setExpandSummaryProducts(s => {
      const v = [...s];
      if (v.includes(product))
        v.splice(v.indexOf(product));
      else
        v.push(product);

      return v;
    })
  };

  useEffect(() => {
    if (downloadedDocument) {
      try {
        saveAs(
          downloadedDocument as Blob,
          downloadDocumentArgs?.documentName || "downloaded_document.pdf"
        );
        setDownloadDocumentRequest(undefined);
      } catch (error) {
        // TODO: Add error handling UI
        console.error("Error downloading the document:", error);
      }
    }
  }, [downloadedDocument]);

  const downloadDocument = (policy: AmendPolicy|undefined, documentName: string) => {
    if (policy && documents) {
        var doc = documents.find(d => d.entityIdentifier == policy.uniqueIdentifier && d.currentVersion?.categoryId == environment.settings.vagDocumentCategory && d.currentVersion.clientVisible);
      if (doc) {
        setDownloadDocumentRequest({
          entityType: doc.entityType.toString(),
          entityIdentifier: doc.entityIdentifier,
          documentId: doc.id.toString(),
          documentName: documentName.match(fileExtensionRegex)
            ? documentName
            : [documentName || "downloaded_document", "pdf"].join("."),
        });
      }
    }
  };

  if (isAmendmentFailed) {
    setTimeout(() => {
      navigate("/");
    }, 1500);

    // TODO: Show error screen
    return <>Oops!</>;
  }

  if (isAmendmentSuccess) {
    if (policiesToAmend){
      const ids = policiesToAmend.filter((p) => selectedPolicies.includes(p.productTypeId === ppi ? "PPI" : "DI")).map(x => x.id);
      localStorage.setItem(
        "policiesAmended",
        JSON.stringify({time: new Date(), policies: ids ?? []})
      );

      // after 30 seconds clear the local storage
      // async processing should be done
      setTimeout(() => {
        localStorage.removeItem("policiesAmended");
      }, 30_000);
    }

    setTimeout(() => {
      navigate("/");
    }, 5000);

    return (
      <SplashMessage backgroundTransparency={0}>
        <div className="flex flex-col items-center gap-6">
          <ShieldCheck className={"w-32 h-32 stroke-splash stroke-2"} />
          <h1 className="splash-message-title">{l10n.notice.yourProductIsActivated}</h1>
          <div
            className="text-center text-lg"
            dangerouslySetInnerHTML={{
              __html: l10n.notice.thankYouForApplicationFindYourConfirmation,
            }}
          />
        </div>
      </SplashMessage>
    );
  }

  if (isDataLoading)
    return (
      <div className="loading-screen flex flex-col justify-center items-center">
        <div className="text-body text-xl">{l10n.common.loading}</div>
      </div>
    );

  return (
    <>
      <h2>{stepsDefinition[step - 1]?.title}</h2>

      {step <= stepsDefinition.length && (
        <>
          <div className="w-full">
            {/* Content for each step */}
            {step <= stepsDefinition.length && (
              <>
                {/* One policy and first step */}
                {policiesToAmend?.length === 1 && step === 1 && (
                  <div>
                    <PolicyAmendment
                      details={policiesToAmend[step - 1]}
                      onValidityChange={(isValid) => {
                        setAsValid(isValid);
                      }}
                    />
                  </div>
                )}
                {/* Two policies and first two (product) steps */}
                {policiesToAmend?.length === 2 && (step === 1 || step === 2) && (
                  <div>
                    <PolicyAmendment
                      details={policiesToAmend[step - 1]}
                      onValidityChange={(isValid) => {
                        setAsValid(isValid);
                      }}
                    />
                  </div>
                )}
                {/* Final (summary) step */}
                {((policiesToAmend?.length === 2 && step === 3) || (policiesToAmend?.length === 1 && step === 2)) && (
                  <div className="mb-6 flex flex-col">
                    <p>{l10n.notice.thankYouYouCanNowSubmitMembershipApplication}</p>
                    {policiesToAmend
                      ?.filter((p) =>
                        selectedPolicies.includes(p.productTypeId === ppi ? "PPI" : "DI")
                      )
                      .map((p) => (
                          <div key={p.id} className="card mb-4">
                          <div className="card-body pb-4">
                            <div className="mb-4 last:mb-0" key={p.summary}>
                              <div
                                className="flex items-center justify-between"
                                onClick={() => {
                                  expandProductSummary(p.summary);
                                }}
                              >
                                <div className="flex items-center">
                                  <Insurance className={"w-5 h-5 stroke-card stroke-1 mr-2"} />
                                  <span className="text-xl font-medium leading-none">
                                    {l10n.products[productIdToTypeKeyDict[p.productTypeId]].name}
                                  </span>
                                </div>
                                <ChevronRight
                                  className={`w-5 h-5 stroke-card stroke-1 ${summaryExpandedProducts.includes(p.summary) ? "rotate-90" : undefined}`}
                                />
                              </div>
                            </div>
                          </div>
                          {summaryExpandedProducts.includes(p.summary) && (
                            <div className="card-body pt-4">
                              <div className="pb-2 grid grid-cols-2 gap-2 leading-tight border-b border-b-card-body">
                                <span className="flex items-center">
                                  {p.policyCalculationDetails.CoverageFrequency == "Total"
                                    ? l10n.label.coverageTotal
                                    : l10n.label.coveragePerMonth}
                                </span>
                                <span className="flex justify-end items-center font-semibold">
                                  {Formatters.formatCurrencyNumber(
                                    p.policyCalculationDetails.CoverageAmount,
                                    true,
                                    undefined,
                                    0
                                  )}
                                </span>
                              </div>
                              <div className="mt-3 pb-2 grid grid-cols-2 gap-2 leading-tight border-b border-b-card-body">
                                <span className="flex items-center">{l10n.policy.policyStart}</span>
                                <span className="flex justify-end items-center font-semibold">
                                  {Formatters.formatShortDate(p.validFromDate)}
                                </span>
                              </div>
                              <div className="mt-3 pb-2 grid grid-cols-2 gap-2 leading-tight border-b border-b-card-body">
                                <span className="flex items-center">
                                  {l10n.label.premiumPerMonth}
                                </span>
                                <span className="flex justify-end items-center font-semibold">
                                  {Formatters.formatCurrencyNumber(
                                    (p.summary == "DI" ? monthlyPriceDi : monthlyPricePpi) ?? 0
                                  )}
                                </span>
                              </div>
                              {p.numberOfInstallments !== 1 && (
                                <div className="mt-3 pb-2 grid grid-cols-2 gap-2 leading-tight border-b border-b-card-body">
                                  <span
                                    className="flex items-center"
                                    dangerouslySetInnerHTML={{ __html: l10n.label.serviceFees }}
                                  ></span>
                                  <span className="flex justify-end items-center font-semibold">
                                    {Formatters.formatCurrencyNumber(20 / 3)}
                                  </span>
                                </div>
                              )}
                              <div className="mt-3 pb-2 grid grid-cols-2 gap-2 leading-tight">
                                <span className="flex items-center">
                                  {p.numberOfInstallments === 4
                                    ? l10n.label.premiumPerQuarter
                                    : l10n.label.premiumPerYear}
                                </span>
                                <span className="flex justify-end items-center font-semibold">
                                  {Formatters.formatCurrencyNumber(
                                    (p.summary == "DI"
                                      ? monthlyGrossInstallmentPriceDi
                                      : monthlyGrossInstallmentPricePpi) ?? 0
                                  )}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                    <div className="w-11/12 self-center flex flex-1 justify-between items-baseline mb-6">
                      <span className="text-body text-lg leading-none">
                        {l10n.label.totalPremiumPerMonth}
                      </span>
                      <span className="text-xl font-semibold">
                        {Formatters.formatCurrencyNumber(totalMonthlyPrice)}
                      </span>
                    </div>

                    <div className="card">
                      <div className="card-body">
                        <p className="mb-3 text-lg font-medium">
                          {l10n.policy.additionalDocumentApprovals}
                        </p>

                        {/* ["gtc", "vag45"] */}
                        <div className="mb-3 pb-3 last:pb-0 last:mb-0 flex justify-between items-center border-b border-b-card-body last:border-b-0">
                          <div className="flex items-center">
                            <a
                              target="_blank"
                              href={(() => {
                                return selectedPolicies.length == 2
                                  ? l10n.decls["gtc"].link
                                  : selectedPolicies.at(0) === "PPI"
                                    ? l10n.decls["gtcPPI"].link
                                    : l10n.decls["gtcDI"].link;
                              })()}
                              className="flex items-center"
                            >
                              <DocumentText className={"w-5 h-5 stroke-2 mr-2"} />
                              <span className="leading-none">{l10n.decls["gtc"].name}</span>
                            </a>
                          </div>
                        </div>
                        <div className="mb-3 pb-3 last:pb-0 last:mb-0 flex justify-between items-center border-b border-b-card-body last:border-b-0">
                          <div className="flex items-center">
                            <a
                              className="flex items-center cursor-pointer"
                              onClick={() => downloadDocument(policiesToAmend.at(0), "VAG45.pdf")}
                            >
                              <DocumentText className={"w-5 h-5 stroke-2 mr-2"} />
                              <span className="leading-none">{l10n.decls["vag45"].name}</span>
                            </a>
                          </div>
                        </div>

                        {/* {finalDeclarations?.map((x) => (
                          <div className="mb-3 pb-3 last:pb-0 last:mb-0 flex justify-between items-center border-b border-b-[var(--petrol2)] last:border-b-0" key={x}>
                            <div className="flex items-center">
                              <a
                                href={l10n.decls[x].link}
                                className="flex items-center"
                                download={true}
                              >
                                <DocumentText className={"w-5 h-5 stroke-2 mr-2"} />
                                <span className="leading-none">{l10n.decls[x].name}</span>
                              </a>
                            </div>
                          </div>
                        ))} */}
                      </div>
                    </div>

                    <div className="form-check">
                      <input
                        checked={finalApproval}
                        className="form-check-input"
                        id="truthDeclaration"
                        name="truthDeclaration"
                        onChange={() => {
                          setFinalApproval(!finalApproval);
                          setAsValid(!finalApproval);
                        }}
                        required
                        title="The above information is truthful to the best of my knowledge."
                        type="checkbox"
                      />
                      <label className="form-check-label" htmlFor="truthDeclaration">
                        {l10n.label.byCheckingBoxReadUnderstoodTermsAndConditions}
                      </label>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Buttons (same for every step) */}
            <div className="btn-group">
              <button
                className="btn btn-primary"
                disabled={!isValid || (isSummaryStep && isAmendmentLoading)}
                onClick={() => changeStep("forward")}
              >
                {isSummaryStep
                  ? isAmendmentLoading
                    ? l10n.common.loading
                    : l10n.btn.submitApplication
                  : l10n.common.next}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  changeStep("cancel");
                }}
              >
                {l10n.btn.cancel}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};
